import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';

/**
 * Liberty Square (自由廣場) and the National Chiang Kai-shek Memorial Hall (中正紀念堂), oriented as in
 * reality: the white gate at the west end, the hall with its 89 steps at the east end, the National
 * Theater on the north side of the square and the National Concert Hall on the south side. The koi
 * ponds 光華池 (north) and 雲漢池 (south) lie in the gardens beside the hall, inside blue-tiled corridors.
 * The MRT exit is in the south-west corner.
 *
 * Big landmarks are bottom-anchored decor drawn by src/render/propsCks.ts; the tall hall is split into
 * two slices (variant 0/1) so neither is culled while part of it is on screen.
 */
const W = 100, H = 60;
const b = new MapBuilder(W, H, 'g');
// Outer edge: the white perimeter wall (decor) stands on a hedge strip.
b.fill(0, 0, W, 3, 'h').fill(0, H - 3, W, 3, 'h').fill(0, 0, 2, H, 'h').fill(W - 2, 0, 2, H, 'h');
// The great square and the forecourt in front of the gate.
b.fill(20, 17, 51, 26, '7');
b.fill(4, 34, 16, 9, '7');
// Liberty Boulevard: the granite axis from the gate to the steps.
b.fill(20, 26, 44, 8, 'X');
// Under the gate (solid decor covers it) and the hall grounds.
b.fill(2, 26, 18, 8, '7');
b.fill(64, 17, 34, 26, '7');
b.fill(95, 17, 3, 26, 'h');
// Theater (north) and Concert Hall (south) plinths, with their front terraces.
b.fill(23, 6, 23, 11, '7');
b.fill(23, 43, 23, 10, '7');
b.fill(20, 52, 30, 3, '8');
// Garden paths (red brick).
b.fill(6, 4, 2, 30, '8'); // west garden walk
b.fill(4, 10, 12, 6, '8'); // tai chi plaza
b.fill(46, 5, 2, 12, '8').fill(46, 43, 2, 12, '8');
b.fill(48, 14, 28, 3, '8').fill(48, 43, 50, 2, '8');
b.fill(76, 12, 19, 8, 'g'); // behind the hall (under its roof)
b.fill(6, 43, 3, 14, '8'); // MRT exit path
b.fill(9, 54, 11, 2, '8');
// Covered corridors along the north and south walls (floor under the roofs).
b.fill(48, 3, 50, 2, '8').fill(48, 55, 50, 2, '8');
b.fill(48, 3, 2, 12, '8').fill(48, 45, 2, 12, '8');
// The two koi ponds.
b.fill(53, 5, 15, 8, '9').fill(56, 13, 9, 1, '9');
b.fill(53, 46, 15, 8, '9').fill(55, 54, 11, 1, '9').fill(56, 45, 9, 1, '9');
// Bridges across the ponds.
b.fill(60, 5, 2, 9, 'y');
b.fill(60, 45, 2, 10, 'y');
// Paths round the ponds.
b.fill(70, 5, 2, 10, '8').fill(70, 45, 2, 10, '8');
// Flowerbeds and lawns.
b.fill(10, 20, 6, 3, 'G').fill(10, 46, 6, 3, 'G');
b.fill(74, 6, 8, 3, 'G').fill(74, 51, 8, 3, 'G');

const at = (tx: number, ty: number) => ({ x: px(tx), y: px(ty) });
type TreeKind = 'tree' | 'cherry' | 'pine';
const tree = (tx: number, ty: number, kind: TreeKind = 'tree'): Decor => ({ kind, ...at(tx, ty), solid: { x: px(tx) - 6, y: px(ty) - 6, w: 12, h: 6 } });
const lamp = (tx: number, ty: number): Decor => ({ kind: 'ck_lamp', ...at(tx, ty), solid: { x: px(tx) - 3, y: px(ty) - 4, w: 6, h: 4 } });

// Landmark anchors (pixels).
const GATE = { x: px(11) + 8, y: px(34) };
const HALL = { x: px(85) + 8, y: px(40) };
const THEATER = { x: px(34) + 8, y: px(16) };
const CONCERT = { x: px(34) + 8, y: px(52) };

const decor: Decor[] = [
  // --- Landmarks -----------------------------------------------------------------------------
  { kind: 'ck_gate', ...GATE, w: 340, solid: { x: px(1), y: px(26), w: px(21) + 6, h: px(8) - 2 } },
  { kind: 'ck_bridge', x: px(61), y: px(14), h: px(9) },
  { kind: 'ck_bridge', x: px(61), y: px(55), h: px(10) },
  // Memorial Hall: three 150px slices of one picture (platform, walls, the octagonal roof), so none is
  // culled while part of the hall is on screen. The honour guards are drawn inside the slices.
  { kind: 'ck_hall', ...HALL, w: 340, variant: 0, solid: { x: px(76), y: px(12), w: px(19), h: px(28) } },
  ...[1, 2, 3].map((k): Decor => ({ kind: 'ck_hall', x: HALL.x, y: HALL.y - 150 * k, w: 340, variant: k })),
  // The 89 steps, sorted at their own south edge.
  { kind: 'ck_stairs', x: px(70), y: px(34), w: px(12), solid: { x: px(64) - 4, y: px(26), w: px(12) + 4, h: px(8) } },
  { kind: 'ck_theater', ...THEATER, w: 340, variant: 0, solid: { x: px(23), y: px(6), w: px(23), h: px(10) } },
  { kind: 'ck_theater', ...CONCERT, w: 340, variant: 1, solid: { x: px(23), y: px(42), w: px(23), h: px(10) } },
  // Perimeter wall with blue tiles and the corridors.
  ...[0, 1, 2, 3, 4, 5, 6].map((i): Decor => ({ kind: 'ck_wall', x: px(2 + i * 14 + 7), y: px(3), w: px(14) })),
  ...[0, 1, 2, 3, 4, 5, 6].map((i): Decor => ({ kind: 'ck_wall', x: px(2 + i * 14 + 7), y: px(58), w: px(14), variant: 1 })),
  ...[0, 1, 2, 3, 4].map((i): Decor => ({ kind: 'ck_corridor', x: px(48 + i * 10 + 5), y: px(5), w: px(10), solid: { x: px(48 + i * 10) + 1, y: px(5) - 3, w: 3, h: 3 } })),
  ...[0, 1, 2, 3, 4].map((i): Decor => ({ kind: 'ck_corridor', x: px(48 + i * 10 + 5), y: px(57), w: px(10), solid: { x: px(48 + i * 10) + 1, y: px(57) - 3, w: 3, h: 3 } })),
  // Moon-gate signboards at the garden entrances.
  { kind: 'ck_sign', x: px(19), y: px(44) + 4, variant: 0 },
  { kind: 'ck_sign', x: px(47), y: px(17) + 4, variant: 1 },
  { kind: 'ck_sign', x: px(47), y: px(45) + 2, variant: 2 },
  // --- Life ----------------------------------------------------------------------------------
  { kind: 'ck_koi', x: px(57), y: px(10), w: px(10), variant: 0 },
  { kind: 'ck_koi', x: px(65), y: px(8), w: px(6), variant: 1 },
  { kind: 'ck_koi', x: px(57), y: px(50), w: px(10), variant: 2 },
  { kind: 'ck_koi', x: px(65), y: px(52), w: px(6), variant: 3 },
  { kind: 'ck_lotus', x: px(54) + 8, y: px(7), variant: 0 },
  { kind: 'ck_lotus', x: px(66), y: px(11), variant: 1 },
  { kind: 'ck_lotus', x: px(54) + 8, y: px(52), variant: 1 },
  { kind: 'ck_lotus', x: px(66), y: px(48), variant: 0 },
  { kind: 'ck_pigeons', x: px(42), y: px(38), w: 70 },
  { kind: 'ck_pigeons', x: px(28), y: px(22), w: 60, variant: 1 },
  { kind: 'ck_pigeons', x: px(74), y: px(36), w: 50, variant: 2 },
  { kind: 'ck_kites', x: px(16), y: px(52), w: 160 },
  { kind: 'ck_kites', x: px(90), y: px(12), w: 120, variant: 1 },
  { kind: 'ck_taichi', x: px(10), y: px(14), w: 120 },
  { kind: 'ck_dancers', x: px(30), y: px(19) + 8, w: 120 },
  { kind: 'ck_dancers', x: px(40), y: px(55), w: 100, variant: 1 },
  { kind: 'ck_school', x: px(52), y: px(36), w: 110 },
  { kind: 'ck_tourists', x: px(14), y: px(39), w: 80 },
  { kind: 'ck_tourists', x: px(68), y: px(38), w: 80, variant: 1 },
  { kind: 'ck_tourists', x: px(38), y: px(29), w: 80, variant: 2 },
  { kind: 'ck_tourists', x: px(57), y: px(31), w: 80, variant: 1 },
  { kind: 'ck_school', x: px(30), y: px(40), w: 110, variant: 1 },
  { kind: 'ck_balloons', x: px(25), y: px(31), w: 40 },
  ...[31, 43, 55].flatMap((tx): Decor[] => [
    { kind: 'planter', x: px(tx), y: px(24) + 4, solid: { x: px(tx) - 12, y: px(24) - 4, w: 24, h: 8 } },
    { kind: 'planter', x: px(tx), y: px(37) + 4, solid: { x: px(tx) - 12, y: px(37) - 4, w: 24, h: 8 } },
  ]),
  // --- Street furniture ------------------------------------------------------------------------
  ...[22, 28, 34, 40, 46, 52, 58].flatMap((tx) => [lamp(tx, 25), lamp(tx, 35)]),
  lamp(5, 33), lamp(18, 42), lamp(66, 24), lamp(66, 36), lamp(74, 17), lamp(74, 43),
  { kind: 'museumBench', x: px(37), y: px(24), solid: { x: px(37) - 12, y: px(24) - 6, w: 24, h: 6 } },
  { kind: 'museumBench', x: px(25), y: px(37), solid: { x: px(25) - 12, y: px(37) - 6, w: 24, h: 6 } },
  { kind: 'trashBin', x: px(20) + 8, y: px(24) },
  { kind: 'trashBin', x: px(62), y: px(37) },
  { kind: 'trashBin', x: px(9), y: px(44) },
  { kind: 'vendingDeco', x: px(4) + 8, y: px(46), solid: { x: px(4), y: px(46) - 6, w: 16, h: 6 } },
  { kind: 'bikeRack', x: px(14), y: px(56) },
  // --- Trees and gardens -----------------------------------------------------------------------
  // West garden (tai chi and kites), between the gate and the theater.
  ...([[3, 6], [10, 5], [14, 7], [18, 5], [3, 18], [12, 18], [17, 22], [4, 24], [11, 24], [19, 13]] as const).map(([x, y]) => tree(x, y, 'pine')),
  tree(15, 10, 'cherry'), tree(3, 12, 'cherry'), tree(20, 9),
  // South-west garden.
  ...([[3, 47], [16, 45], [20, 49], [4, 56], [19, 56]] as const).map(([x, y]) => tree(x, y)),
  tree(11, 47, 'cherry'), tree(21, 45, 'cherry'),
  // Rows of trees lining the square's north and south edges.
  ...[21, 47, 50].map((tx) => tree(tx, 16)),
  ...[21, 50].map((tx) => tree(tx, 44, 'pine')),
  // The hall gardens.
  ...([[51, 15], [69, 15], [74, 10], [82, 5], [88, 8], [94, 5], [52, 4]] as const).map(([x, y]) => tree(x, y, 'pine')),
  ...([[51, 45], [69, 45], [74, 48], [82, 54], [88, 50], [94, 54], [93, 46], [83, 46], [52, 56]] as const).map(([x, y]) => tree(x, y, 'pine')),
  tree(73, 13, 'cherry'), tree(77, 47, 'cherry'), tree(96, 10), tree(90, 44),
  { kind: 'bushDeco', x: px(47), y: px(26) },
  { kind: 'bushDeco', x: px(47), y: px(34) },
  ...[64, 67].flatMap((tx) => [{ kind: 'bushDeco' as const, x: px(tx), y: px(22) }, { kind: 'bushDeco' as const, x: px(tx), y: px(39) }]),
];

// ====================================================================================================
// Life on the square (src/render/streetLife.ts via propsCksSquare.ts): crowds, sitters, parterres, kites,
// pigeons, the flagpole, the relief guards marching on the hour, show posters outside the theatres.
// ====================================================================================================
type Dir = 0 | 1 | 2 | 3;
const sb = (x: number, y: number, w: number, h: number) => ({ x, y, w, h });
/** mode: 0 mixed, 1 shoppers, 2 tourists, 3 travellers, 4 hikers, 5 facing up, 6 down, 7 left, 8 right. */
const crowd = (tx: number, ty: number, n: number, w: number, h: number, mode = 0, seed = 0): Decor => ({ kind: 'ck_crowd', x: px(tx), y: px(ty), w, h, variant: n + 16 * seed + 256 * mode });
const one = (tx: number, ty: number, look: number, facing: Dir, acc = 0, solid = true): Decor => ({ kind: 'ck_person', x: px(tx), y: px(ty), variant: acc * 128 + (look % 32) * 4 + facing, ...(solid ? { solid: sb(px(tx) - 4, px(ty) - 3, 8, 3) } : {}) });
const benchSit = (tx: number, ty: number, seed = 0): Decor => ({ kind: 'ck_benchSit', x: px(tx), y: px(ty), w: 30, variant: seed, solid: sb(px(tx) - 15, px(ty) - 10, 30, 10) });
const parterre = (tx: number, ty: number, w: number, h: number, v = 0): Decor => ({ kind: 'ck_parterre', x: px(tx), y: px(ty), w, h, variant: v, solid: sb(px(tx) - w / 2 - 2, px(ty) - h - 2, w + 4, h + 2) });
const pigeons = (tx: number, ty: number, v: number, w = 26): Decor => ({ kind: 'ck_pigeons2', x: px(tx), y: px(ty), w, variant: v });
const leaves = (tx: number, ty: number, v: number): Decor => ({ kind: 'ck_leaves', x: px(tx), y: px(ty), w: 34, variant: v });
decor.push(
  // Formal flower beds: along the north and south strips of the square and in the gardens.
  parterre(42, 20.6, 96, 28, 0), parterre(60, 20.6, 96, 28, 2), parterre(36, 41.6, 96, 28, 3), parterre(54, 41.6, 96, 28, 1),
  parterre(13, 23, 96, 44, 4), parterre(13, 49, 96, 44, 0), parterre(78, 9, 120, 44, 2), parterre(78, 54, 120, 44, 3),
  // The national flagpole at the end of the boulevard, and the relief guards.
  { kind: 'ck_flagpole', x: px(62) + 8, y: px(24.6), h: 84, solid: sb(px(62) + 2, px(24.6) - 5, 12, 5) },
  { kind: 'ck_guardWalk', x: px(23), y: px(30) + 8, w: px(39) },
  // Morning tai chi on the square itself, kites, pigeons.
  { kind: 'ck_taichi', x: px(34), y: px(21.5), w: 120, variant: 1 },
  { kind: 'ck_taichi', x: px(60), y: px(40), w: 120, variant: 2 },
  { kind: 'ck_kites', x: px(50), y: px(22.6), w: 120, variant: 2 },
  { kind: 'ck_pigeons', x: px(60), y: px(23.4), w: 60, variant: 4 },
  { kind: 'ck_pigeons', x: px(30), y: px(36.6), w: 60, variant: 5 },
  pigeons(22, 20, 1), pigeons(66, 40, 2), pigeons(7, 40.6, 3), pigeons(48, 32, 4, 40), pigeons(82, 44.6, 5),
  // Crowds: photographing the gate, admiring the hall, strolling the boulevard, under the galleries.
  crowd(25.5, 30, 5, 30, 40, 7, 1), crowd(58, 29.5, 4, 24, 36, 8, 2), crowd(40, 31, 5, 60, 30, 0, 3), crowd(48, 23.6, 4, 40, 8, 2, 4),
  crowd(64, 38.6, 3, 24, 6, 2, 5), crowd(36, 36.4, 3, 30, 6, 1, 6), crowd(69.5, 35.6, 4, 40, 5, 5, 7), crowd(70, 22.6, 4, 36, 8, 2, 8),
  crowd(12, 37, 5, 50, 8, 5, 9), crowd(9, 35.4, 3, 24, 6, 0, 10), crowd(32, 28.4, 4, 60, 20, 0, 5), crowd(50, 31.5, 4, 50, 20, 2, 6),
  crowd(60, 4.7, 3, 40, 3, 0, 11), crowd(84, 4.7, 3, 40, 3, 2, 12), crowd(66, 56.5, 3, 40, 3, 2, 13), crowd(90, 56.5, 2, 24, 3, 0, 14),
  crowd(34.5, 16.6, 4, 60, 4, 5, 15), crowd(34.5, 43.7, 3, 40, 4, 6, 1),
  one(9, 38.8, 8, 1, 8), one(15.2, 38.4, 22, 1, 4), one(44, 27.4, 5, 3, 2), one(30, 33.6, 13, 0, 5), one(52, 26.6, 19, 2, 1), one(20.5, 38.6, 27, 0, 7),
  one(72.5, 40.6, 2, 1, 4), one(56, 15.6, 30, 0, 0), one(36, 26.8, 3, 3, 2, false), one(45.5, 33.2, 21, 2, 7, false), one(27, 25.5, 9, 0, 5, false), one(50, 45.4, 11, 3, 2), one(4.5, 30.5, 16, 0, 1, false), one(96, 30, 24, 1, 0),
  { kind: 'ck_stroller', x: px(44), y: px(35.6), w: -1, variant: 1 }, { kind: 'ck_stroller', x: px(8), y: px(20.6), w: 1, variant: 3 },
  // Benches with people, replacing some of the plain ones, and more in the gardens.
  benchSit(25, 24.4, 1), benchSit(49, 24.4, 2), benchSit(37, 37.4, 3), benchSit(49, 37.4, 4),
  benchSit(12, 17.6, 5), benchSit(14.5, 25.6, 6), benchSit(16.5, 41.8, 7), benchSit(70, 24.8, 8), benchSit(70, 41.2, 9), benchSit(90, 42.4, 10), benchSit(8, 46.6, 11),
  // Show posters outside the National Theater and the Concert Hall.
  { kind: 'ck_poster', x: px(31), y: px(17.6), variant: 0, solid: sb(px(31) - 11, px(17.6) - 3, 22, 3) },
  { kind: 'ck_poster', x: px(37.5), y: px(17.6), variant: 2, solid: sb(px(37.5) - 11, px(17.6) - 3, 22, 3) },
  { kind: 'ck_poster', x: px(31), y: px(52.6), variant: 1, solid: sb(px(31) - 11, px(52.6) - 3, 22, 3) },
  { kind: 'ck_poster', x: px(37.5), y: px(52.6), variant: 3, solid: sb(px(37.5) - 11, px(52.6) - 3, 22, 3) },
  // More palace lamps on the square's north and south strips (the square is lit all night).
  lamp(30, 19.5), lamp(50, 19.5), lamp(44, 40.5), lamp(62, 40.5),
  // Fallen leaves under the tree rows.
  leaves(21.5, 17.6, 1), leaves(48.5, 17.4, 2), leaves(21, 43.4, 3), leaves(50.5, 43.6, 4), leaves(8, 8.6, 5), leaves(20, 50.6, 6), leaves(88, 9.6, 7), leaves(84, 48.6, 8),
);

const warm = '255,224,150';
const lights: Light[] = [
  ...[22, 28, 34, 40, 46, 52, 58].flatMap((tx) => [{ x: px(tx), y: px(25) - 30, r: 60, color: warm }, { x: px(tx), y: px(35) - 30, r: 60, color: warm }]),
  ...([[5, 33], [18, 42], [66, 24], [66, 36], [74, 17], [74, 43]] as const).map(([tx, ty]) => ({ x: px(tx), y: px(ty) - 30, r: 56, color: warm })),
  // The floodlit hall, gate and theaters.
  { x: HALL.x, y: HALL.y - 90, r: 190, color: '235,240,255' },
  { x: px(70), y: px(30), r: 90, color: '235,240,255' },
  { x: GATE.x, y: GATE.y - 50, r: 130, color: '235,240,255' },
  { x: THEATER.x, y: THEATER.y - 40, r: 150, color: '255,220,140' },
  { x: CONCERT.x, y: CONCERT.y - 40, r: 150, color: '255,220,140' },
  // Corridor lanterns and the MRT exit.
  ...[53, 63, 73, 83, 93].flatMap((tx) => [{ x: px(tx), y: px(4), r: 40, color: '255,200,120' }, { x: px(tx), y: px(56), r: 40, color: '255,200,120' }]),
  { x: px(11) + 8, y: px(51), r: 60, color: '200,230,255' },
  // The flagpole and the theatre posters.
  { x: px(62) + 8, y: px(21), r: 50, color: '255,240,200' },
  ...([[31, 17.6], [37.5, 17.6], [31, 52.6], [37.5, 52.6]] as const).map(([tx, ty]) => ({ x: px(tx), y: px(ty) - 28, r: 36, color: '255,236,190' })),
  ...([[30, 19.5], [50, 19.5], [44, 40.5], [62, 40.5]] as const).map(([tx, ty]) => ({ x: px(tx), y: px(ty) - 30, r: 60, color: warm })),
];

export const cks: MapDef = {
  id: 'cks',
  w: b.w,
  h: b.h,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#f4f2ec', wallLight: '#ffffff', wainscot: '#2a5aa8', wallTop: '#1f3a6e' },
  doors: [],
  decor,
  areas: [
    { key: 'ck_hall', rect: { x: px(62), y: px(17), w: px(36), h: px(26) } },
    { key: 'ck_gate', rect: { x: 0, y: px(24), w: px(20), h: px(19) } },
    { key: 'ck_theater', rect: { x: px(20), y: px(3), w: px(28), h: px(14) } },
    { key: 'ck_concert', rect: { x: px(20), y: px(43), w: px(28), h: px(14) } },
    { key: 'ck_guanghua', rect: { x: px(48), y: px(3), w: px(50), h: px(14) } },
    { key: 'ck_yunhan', rect: { x: px(48), y: px(43), w: px(50), h: px(14) } },
    { key: 'ck_square', rect: { x: px(20), y: px(17), w: px(44), h: px(26) } },
    { key: 'ck_garden', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: { mrt: { x: px(11) + 8, y: px(54) + 8, facing: 'up' } },
};
